import { jsx as _jsx } from "react/jsx-runtime";
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as React from 'react';
import { cn } from '../lib/cn';
/**
 * A tooltip is a hint, never the only place something is said. It does not
 * appear on touch and it does not appear for a screen reader unless the trigger
 * is focusable — so anything a user *needs* belongs in the label or the helper
 * text, and this is for the rest.
 *
 * ## What the primitive does for you, verified against `@radix-ui/react-tooltip` 1.2.16
 *
 * Three behaviours are load-bearing for `PLT-007` AC-3 and are the primitive's, not this file's.
 * They are written down because a hover affordance that is *only* a hover affordance excludes
 * keyboard and touch users, and the next reader should not have to re-derive whether Radix already
 * covers that:
 *
 * - **Focus opens it.** `TooltipTrigger` composes `onFocus` → `context.onOpen()` (suppressed only
 *   while a pointer is down, so a click does not leave one hanging). Tabbing to the trigger is
 *   enough; no `onKeyDown` of your own is needed.
 * - **Escape dismisses it.** `TooltipContent` renders inside a `DismissableLayer` whose `onDismiss`
 *   is `onClose`, and that layer's escape-keydown listener is on the document. Pressing Escape
 *   anywhere while it is open closes it.
 * - **A trigger that is `disabled` gets neither.** That is the browser, not Radix: a natively
 *   disabled button is out of the tab order and does not reliably emit pointer events. A control
 *   whose tooltip is the *only* explanation of why it cannot act must therefore be `aria-disabled`
 *   and inert-by-handler, never `disabled`.
 *
 * ## `aria-describedby`, and how to keep one sentence from being announced twice
 *
 * Radix puts `aria-describedby={contentId}` on the trigger **only while the tooltip is open**, and
 * it renders that description nowhere else. For anything a user needs, that is not enough: the
 * tooltip never opens on touch (`onPointerMove` returns early for `pointerType === 'touch'`), so a
 * mobile screen-reader user would meet the control with no description at all.
 *
 * The fix is to carry your own always-present description and point at it. It composes cleanly
 * because the primitive spreads the caller's props **after** its own `aria-describedby` — so an
 * `aria-describedby` you pass to `TooltipTrigger` wins outright and the tooltip's `contentId` is
 * never referenced. Pair that with `aria-hidden` on the `TooltipContent`, and the sentence has one
 * visual channel and one accessibility channel rather than two of each. `WithheldControl` in this
 * package is that whole shape, packaged — reach for it rather than rebuilding it, and read its
 * header for why each half is the way it is.
 */
export const TooltipProvider = TooltipPrimitive.Provider;
export const Tooltip = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;
/**
 * Renders the content into `document.body` rather than beside its trigger.
 *
 * Radix positions the content `fixed`, so it is out of flow either way and cannot push a layout
 * around. What the portal buys is the other failure: a trigger inside an ancestor that establishes
 * a containing block — any `transform`, `filter`, `contain` or `will-change` — clips a `fixed`
 * descendant to that ancestor's box, and a tooltip on a control at the right edge of a narrow band
 * is exactly where that shows up. Portal it and the only box that can clip it is the viewport,
 * which Floating UI's collision detection already keeps it inside.
 */
export const TooltipPortal = TooltipPrimitive.Portal;
export const TooltipContent = React.forwardRef(({ className, sideOffset = 6, ...props }, ref) => (_jsx(TooltipPrimitive.Content, { ref: ref, sideOffset: sideOffset, className: cn('z-overlay max-w-xs rounded-md bg-gray-900 px-3 py-1.5 text-xs text-ivory-50 shadow-md', 'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95', className), ...props })));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;
//# sourceMappingURL=tooltip.js.map